import {
  Avatar,
  Box,
  Flex,
  Loading,
  StyledVideoTile,
  Text,
  Tooltip,
  Video,
  flexCenter,
  styled,
  textEllipsis,
  useBorderAudioLevel,
  useTheme,
} from "@100mslive/react-ui";
import { DoorIcon, SettingsIcon } from "@100mslive/react-icons";
import React, {
  Fragment,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import {
  UserPreferencesKeys,
  defaultPreviewPreference,
  useUserPreferences,
} from "../hooks/useUserPreferences";
import {
  selectIsLocalVideoEnabled,
  selectLocalPeer,
  selectVideoTrackByID,
  useAVToggle,
  useHMSStore,
  usePreviewJoin,
} from "@100mslive/react-sdk";

import { AudioVideoToggle } from "../AudioVideoToggle";
import IconButton from "../../IconButton";
import { Image } from "../Image";
import { LiveHodoContext } from "../../contexts/LiveHodoContext";
import PreviewName from "./PreviewName";
import { ROLES } from "../../common/roles";
import SettingsModal from "../Settings/SettingsModal";
import TileConnection from "../Connection/TileConnection";
import { UI_SETTINGS } from "../../common/constants";
import { VirtualBackground } from "../../plugins/VirtualBackground/VirtualBackground";
import { getImageURL } from "../../common/utils";
import { useGetUserData } from "../../hooks/useGetUserData";
import { useParams } from "react-router-dom";
import { useUISettings } from "../AppData/useUISettings";
import { useTranslation } from "react-i18next";

const PreviewJoin = ({
  token,
  onJoin,
  env,
  skipPreview,
  initialName,
  asRole,
}) => {
  const { t } = useTranslation();
  const [previewPreference, setPreviewPreference] = useUserPreferences(
    UserPreferencesKeys.PREVIEW,
    defaultPreviewPreference
  );
  const [name, setName] = useState(initialName || previewPreference.name);
  const { isLocalAudioEnabled, isLocalVideoEnabled } = useAVToggle();
  const [previewError, setPreviewError] = useState(false);
  const { enableJoin, preview, join } = usePreviewJoin({
    name,
    token,
    initEndpoint: env ? `https://${env}-init.100ms.live/init` : undefined,
    initialSettings: {
      isAudioMuted: skipPreview || previewPreference.isAudioMuted,
      isVideoMuted: skipPreview || previewPreference.isVideoMuted,
      speakerAutoSelectionBlacklist: ["Yeti Stereo Microphone"],
    },
    captureNetworkQualityInPreview: true,
    handleError: (_, method) => {
      if (method === "preview") {
        setPreviewError(true);
      }
    },
    asRole,
  });
  const savePreferenceAndJoin = useCallback(() => {
    setPreviewPreference({
      name,
      // isAudioMuted: !isLocalAudioEnabled,
      isVideoMuted: !isLocalVideoEnabled,
    });
    join();
    onJoin && onJoin();
  }, [
    join,
    isLocalAudioEnabled,
    isLocalVideoEnabled,
    name,
    setPreviewPreference,
    onJoin,
  ]);
  useEffect(() => {
    if (token) {
      if (skipPreview) {
        savePreferenceAndJoin();
      } else {
        preview();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, skipPreview]);
  return (
    <Container>
      <Text variant="h4" css={{ wordBreak: "break-word", textAlign: "center" }}>
        {t("lbl_hodo_community_events")}
      </Text>
      <Text
        css={{ c: "$textMedEmp", my: "$6", textAlign: "center" }}
        variant="body1"
      >
        {t("lbl_setup_sound_and_video")}
      </Text>
      <Flex
        align="center"
        justify="center"
        css={{
          "@sm": { width: "100%" },
          flexDirection: "column",
        }}
      >
        <PreviewTile name={name} error={previewError} />
        <PreviewControls
          enableJoin={enableJoin}
          savePreferenceAndJoin={savePreferenceAndJoin}
        />
        <PreviewName
          name={name}
          onChange={setName}
          enableJoin={enableJoin}
          onJoin={savePreferenceAndJoin}
        />
      </Flex>
    </Container>
  );
};

const Container = styled("div", {
  width: "100%",
  ...flexCenter,
  flexDirection: "column",
  px: "$10",
});

const PreviewTile = ({ name, error }) => {
  const localPeer = useHMSStore(selectLocalPeer);
  const borderAudioRef = useBorderAudioLevel(localPeer?.audioTrack);
  const isVideoOn = useHMSStore(selectIsLocalVideoEnabled);
  const mirrorLocalVideo = useUISettings(UI_SETTINGS.mirrorLocalVideo);
  const trackSelector = selectVideoTrackByID(localPeer?.videoTrack);
  const track = useHMSStore(trackSelector);
  const userData = useGetUserData();

  const {
    aspectRatio: { width, height },
  } = useTheme();
  return (
    <StyledVideoTile.Container
      css={{
        bg: "$surfaceDefault",
        aspectRatio: width / height,
        width: "unset",
        height: "min(360px, 60vh)",
        mt: "$12",
        "@sm": {
          height: "unset",
          width: "min(360px, 100%)",
          maxWidth: "100%",
        },
      }}
      ref={borderAudioRef}
    >
      {localPeer ? (
        <>
          <TileConnection name={name} peerId={localPeer.id} hideLabel={true} />
          <Video
            mirror={track?.facingMode !== "environment" && mirrorLocalVideo}
            trackId={localPeer.videoTrack}
            data-testid="preview_tile"
          />
          {!isVideoOn ? (
            <StyledVideoTile.AvatarContainer
              style={{ width: "90%", height: "90%" }}
            >
              {/* {userData && userData?.avatar ? (
                <Image
                  css={{ r: "$round", size: "50%" }}
                  alt={userData?.name}
                  src={getImageURL(userData?.avatar)}
                />
              ) : (
                <Avatar name={name} data-testid="preview_avatar_tile" />
              )} */}
              <Avatar name={name} data-testid="preview_avatar_tile" />
              <Text css={{ ...textEllipsis("75%") }} variant="body2">
                {name}
              </Text>
            </StyledVideoTile.AvatarContainer>
          ) : null}
        </>
      ) : !error ? (
        <Loading size={100} />
      ) : null}
    </StyledVideoTile.Container>
  );
};

const PreviewControls = () => {
  const { roomId: urlRoomId, role: userRole } = useParams(); // from the url

  return (
    <Flex
      justify="between"
      css={{
        width: "100%",
        mt: "$8",
      }}
    >
      <Flex css={{ gap: "$4" }}>
        <AudioVideoToggle compact />
        <VirtualBackground />
      </Flex>
      <Flex css={{ gap: "$4" }}>
        {/* {userRole === ROLES.GUEST && <PreviewJoinCabin />} */}
        <PreviewSettings />
      </Flex>
    </Flex>
  );
};

const PreviewJoinCabin = React.memo(() => {
  const {
    stateCtx: { isJoinCabin },
    setIsJoinCabin,
  } = useContext(LiveHodoContext);

  return (
    <Fragment>
      <Tooltip side="top" title={`${isJoinCabin ? "Rời" : "Vào"} Cabin`}>
        <IconButton
          active={!isJoinCabin}
          key="toggleJoinCabin"
          data-testid="preview_join_cabin"
          onClick={() => setIsJoinCabin(!isJoinCabin)}
        >
          {isJoinCabin ? <DoorIcon /> : <DoorIcon />}
        </IconButton>
      </Tooltip>
    </Fragment>
  );
});

const PreviewSettings = React.memo(() => {
  const [open, setOpen] = useState(false);
  return (
    <Fragment>
      <IconButton
        data-testid="preview_setting_btn"
        onClick={() => setOpen(value => !value)}
      >
        <SettingsIcon />
      </IconButton>
      {open && <SettingsModal open={open} onOpenChange={setOpen} />}
    </Fragment>
  );
});

export default PreviewJoin;
